import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CreditRequestStatusEnum} from '../../../model/credit/credit-request-status.enum';
import {CreditRequestModel} from '../../../model/credit/credit-request.model';
import {SearchableFilter, SearchableOrder, SearchableState, useSearchableStateWithOrdering} from '../../../state/searchable-state';
import {CreditSupportPageHooks} from './credit-support-page-hooks';

export type CreditSupportPageState = {
  requests: CreditRequestModel[]
  searchableState: SearchableState
  extractAvailableValues: (column: string) => string[]
  total: number

  refreshRequests: () => Promise<void>

  isDrawerOpen: boolean
  selectedRequest: CreditRequestModel | null
  setDrawerOpen: (value: boolean) => void
  setSelectedRequest: (request: CreditRequestModel | null) => void

  isAttachmentDrawerOpen: boolean
  setAttachmentDrawerOpen: (value: boolean) => void

  translateLocal: (key: string) => string
  translate: (key: string) => string
}

export const useCreditSupportPageState = (): CreditSupportPageState => {
  const {t} = useTranslation()

  const availableValues = [
    'credit_request.organization_name',
    'credit_request.account_name',
    'credit_request.module',
    'credit_request.priority',
    'credit_request.status'
  ]
  const fullTextSearchColumns = [
    'credit_request.id',
    'credit_request.title',
    'credit_request.description',
    'credit_request.organization_name',
    'credit_request.account_name'
  ]
  const initialOrdering: SearchableOrder[] = [{
    orderBy: 'credit_request.created_date_timestamp',
    orderValues: [],
    orderDirection: 'DESCENDING'
  }]
  const initialAndFilters: SearchableFilter[] = [{
    column: 'credit_request.status',
    searchOperator: 'IN',
    values: [
      CreditRequestStatusEnum.IN_PROGRESS,
      CreditRequestStatusEnum.NEEDS_ESTIMATION,
      CreditRequestStatusEnum.WAITING_CUSTOMER_ACCEPTANCE,
      CreditRequestStatusEnum.SCHEDULED,
    ]
  }]

  const searchableState = useSearchableStateWithOrdering(availableValues, fullTextSearchColumns, initialOrdering, undefined, initialAndFilters)
  const [requests, setRequests] = CreditSupportPageHooks.useCreditRequests(searchableState.searchable)
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  const [attachmentDrawerOpen, setAttachmentDrawerOpen] = useState<boolean>(false)
  const [selectedRequest, setSelectedRequest] = useState<CreditRequestModel | null>(null)

  const refreshRequests = async () => {
    const refreshed = await CreditSupportPageHooks.fetchCreditRequests(searchableState.searchable)
    if (selectedRequest !== null) {
      const updated = refreshed.items.find(it => it.id === selectedRequest.id) ?? selectedRequest
      setSelectedRequest(updated)
    }
    setRequests(refreshed)
  }

  return {
    requests: requests?.items ?? [],
    total: requests?.total ?? 0,
    searchableState: searchableState,
    isDrawerOpen: drawerOpen,
    selectedRequest: selectedRequest,
    isAttachmentDrawerOpen: attachmentDrawerOpen,
    setAttachmentDrawerOpen: setAttachmentDrawerOpen,
    refreshRequests: refreshRequests,
    setSelectedRequest: setSelectedRequest,
    setDrawerOpen: setDrawerOpen,
    extractAvailableValues: (column: string) =>
      searchableState.extractAvailableValues(requests?.available ?? {}, column),
    translateLocal: (key) => t(`credit-support-page.${key}`),
    translate: (key) => t(key)
  }
}
